import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { AuthLayout, FormError, PasswordInput } from '../components/AuthLayout';
import { API_URL } from '../utils/api';
import { getErrorMessage } from '../utils/errors';
import { useDocumentTitle } from '../hooks/use-document-title';

const MIN_LENGTH = 8;

export default function ResetPasswordPage() {
  useDocumentTitle('Choose a new password');
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  if (!token) {
    return (
      <AuthLayout
        title="Link not valid"
        subtitle="This password reset link is missing or incomplete."
        footer={<Link to="/login" className="text-gold hover:underline font-medium">Back to sign in</Link>}
      >
        <Button asChild className="w-full bg-ink text-paper hover:bg-ink/90">
          <Link to="/forgot-password">Request a new link</Link>
        </Button>
      </AuthLayout>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < MIN_LENGTH) {
      setError(`Use at least ${MIN_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, password });
      toast.success('Password updated. Please sign in.');
      navigate('/login', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'We could not update your password. Please request a new link.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout
      title="Choose a new password"
      footer={<Link to="/forgot-password" className="text-gold hover:underline font-medium">Need a new link?</Link>}
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        <div>
          <Label htmlFor="new-password">New password</Label>
          <PasswordInput
            id="new-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={MIN_LENGTH}
            className="mt-2"
            aria-describedby="password-help"
          />
          <p id="password-help" className="mt-1 text-xs text-ink/60">At least {MIN_LENGTH} characters.</p>
        </div>
        <div>
          <Label htmlFor="confirm-password">Confirm new password</Label>
          <PasswordInput
            id="confirm-password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className="mt-2"
          />
        </div>
        <FormError message={error} />
        <Button type="submit" disabled={saving} className="w-full bg-ink text-paper hover:bg-ink/90">
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {saving ? 'Saving…' : 'Update password'}
        </Button>
      </form>
    </AuthLayout>
  );
}
